import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, RotateCcw, SwitchCamera, X } from 'lucide-react';
import { toaster } from '../components/ui';
import { uploadApi } from '../services/api';
import { Scanner as JSScanner } from '../services/scanner';
import './Scanner.css';

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global { interface Window { cv: any; } }

const OPEN_CV_URL = 'https://docs.opencv.org/4.7.0/opencv.js';
const EMA_ALPHA = 0.25;          // lower = smoother (less jumpy), higher = more responsive
const STABLE_THRESHOLD_PX = 15;
const STABLE_FRAMES_REQUIRED = 22;
const MISS_TOLERANCE = 6;        // frames of failed detection before clearing overlay
const DETECT_EVERY_N_FRAMES = 4; // OpenCV runs on 1 of every N RAF ticks (lower = smoother, costlier)
const LS_CAMERA_KEY = 'paperbrain.lastCameraId';

function pickInitialCamera(cams: MediaDeviceInfo[]): number {
  try {
    const saved = localStorage.getItem(LS_CAMERA_KEY);
    if (saved) {
      const idx = cams.findIndex(c => c.deviceId === saved);
      if (idx >= 0) return idx;
    }
  } catch (_) { /* localStorage may be blocked */ }
  return 0;
}

type Point = { x: number; y: number };
type Corners = { topLeft: Point; topRight: Point; bottomRight: Point; bottomLeft: Point };
type DisplaySize = { width: number; height: number };
type ScanState = 'searching' | 'detected' | 'confirming' | 'captured';

function calculateDisplaySize(vw: number, vh: number): DisplaySize {
  const vW = window.innerWidth || vw;
  const vH = window.innerHeight || vh;
  if (!vW || !vH || !vw || !vh) return { width: vw || vW, height: vh || vH };
  const aspect = vw / vh;
  return aspect > vW / vH
    ? { width: vW, height: vW / aspect }
    : { width: vH * aspect, height: vH };
}

/** Set up a canvas to match video resolution with DPR scaling for sharp display.
 *  Context transform means all draw calls use video-coordinate space. */
function setupPreviewCanvas(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx || !video.videoWidth) return null;
  const dpr = window.devicePixelRatio || 1;
  const tw = Math.round(video.videoWidth * dpr);
  const th = Math.round(video.videoHeight * dpr);
  if (canvas.width !== tw || canvas.height !== th) {
    canvas.width = tw;
    canvas.height = th;
  }
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

/** Set up an offscreen canvas at raw video resolution (no DPR) for cv.imread. */
function setupDetectCanvas(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
  const ctx = canvas.getContext('2d');
  if (!ctx || !video.videoWidth) return null;
  if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
  }
  return ctx;
}

function smoothCorners(prev: Corners | null, next: Corners): Corners {
  if (!prev) return next;
  const lerp = (a: Point, b: Point): Point => ({
    x: EMA_ALPHA * b.x + (1 - EMA_ALPHA) * a.x,
    y: EMA_ALPHA * b.y + (1 - EMA_ALPHA) * a.y,
  });
  return {
    topLeft: lerp(prev.topLeft, next.topLeft),
    topRight: lerp(prev.topRight, next.topRight),
    bottomRight: lerp(prev.bottomRight, next.bottomRight),
    bottomLeft: lerp(prev.bottomLeft, next.bottomLeft),
  };
}

function cornerDelta(a: Corners, b: Corners): number {
  const d = (p1: Point, p2: Point) => Math.hypot(p1.x - p2.x, p1.y - p2.y);
  return Math.max(d(a.topLeft, b.topLeft), d(a.topRight, b.topRight),
    d(a.bottomRight, b.bottomRight), d(a.bottomLeft, b.bottomLeft));
}

function drawOverlay(ctx: CanvasRenderingContext2D, corners: Corners, progress: number) {
  const { topLeft: tl, topRight: tr, bottomRight: br, bottomLeft: bl } = corners;
  ctx.fillStyle = progress > 0 ? `rgba(196, 181, 255, ${0.08 + progress * 0.07})` : 'rgba(196, 181, 255, 0.08)';
  ctx.beginPath();
  ctx.moveTo(tl.x, tl.y); ctx.lineTo(tr.x, tr.y);
  ctx.lineTo(br.x, br.y); ctx.lineTo(bl.x, bl.y);
  ctx.closePath();
  ctx.fill();
  // Mobile's reticle: bright lilac that reads over any camera feed.
  ctx.strokeStyle = progress > 0 ? '#c4b5ff' : 'rgba(196, 181, 255, 0.75)';
  ctx.lineWidth = progress > 0 ? 4 : 3;
  ctx.lineJoin = 'round';
  ctx.stroke();
  ctx.fillStyle = ctx.strokeStyle;
  for (const p of [tl, tr, br, bl]) {
    ctx.beginPath(); ctx.arc(p.x, p.y, 6, 0, Math.PI * 2); ctx.fill();
  }
}

/**
 * Encode the captured scan as JPEG rather than full-resolution PNG.
 *
 * A 2560x1440 PNG scan is 2-8MB; the same page as q0.8 JPEG is a few hundred KB
 * with no meaningful loss for OCR. That matters twice over: the server caps
 * uploads, and OCR base64-encodes the whole image in memory (~3-4x the file size
 * at peak). Mobile already does this — see mobile/src/features/scanner/compress.ts.
 */
const JPEG_QUALITY = 0.8;

async function canvasToFile(canvas: HTMLCanvasElement, filename: string): Promise<File> {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(new File([blob!], filename, { type: 'image/jpeg' })),
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}

const Scanner: React.FC = () => {
  const navigate = useNavigate();
  const scannerRef = useRef(new JSScanner() as any);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const resultCanvasRef = useRef<HTMLCanvasElement>(null);
  const detectCanvasRef = useRef<HTMLCanvasElement>(document.createElement('canvas'));
  const videoRef = useRef<HTMLVideoElement>(null);
  const animFrameRef = useRef(0);
  const smoothedRef = useRef<Corners | null>(null);
  const prevCornersRef = useRef<Corners | null>(null);
  const stableCountRef = useRef(0);
  const missCountRef = useRef(0);
  const frameRef = useRef(0);
  const streamRef = useRef<MediaStream | null>(null);
  const isMountedRef = useRef(true);
  // Bumped on every loadStream call (and on unmount). Stale calls drop their freshly-acquired
  // stream instead of leaking it — critical because StrictMode double-mounts the init effect,
  // which fires two concurrent getUserMedia() requests.
  const streamGenRef = useRef(0);

  const [scanState, setScanState] = useState<ScanState>('searching');
  const [stableProgress, setStableProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [displaySize, setDisplaySize] = useState<DisplaySize | null>(null);
  const [videoDims, setVideoDims] = useState<DisplaySize | null>(null);
  const [loadedCV, setLoadedCV] = useState(false);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [cameraIdx, setCameraIdx] = useState(0);
  const [scanQueue, setScanQueue] = useState<File[]>([]);

  const loadOpenCv = useCallback((cb: () => void) => {
    if (document.getElementById('open-cv') || loadedCV) { setLoadedCV(true); cb(); return; }
    const script = document.createElement('script');
    script.id = 'open-cv'; script.src = OPEN_CV_URL;
    script.onload = () => { setTimeout(cb, 1000); setLoadedCV(true); };
    document.body.appendChild(script);
  }, [loadedCV]);

  const updateDisplaySize = useCallback((vw: number, vh: number) => {
    if (!vw || !vh) return;
    setVideoDims({ width: vw, height: vh });
    setDisplaySize(calculateDisplaySize(vw, vh));
  }, []);

  useEffect(() => {
    const onResize = () => { if (videoDims) setDisplaySize(calculateDisplaySize(videoDims.width, videoDims.height)); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [videoDims]);

  // --- Detection tick ---
  const tick = useCallback(() => {
    const video = videoRef.current;
    const preview = previewCanvasRef.current;
    if (!video || !preview || !video.videoWidth) return;

    // Draw sharp video to DPR-scaled preview canvas
    const pctx = setupPreviewCanvas(preview, video);
    if (!pctx) return;
    pctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    // Skip frames — detect every Nth frame, always draw overlay
    frameRef.current++;
    if (frameRef.current % DETECT_EVERY_N_FRAMES !== 0) {
      if (smoothedRef.current) {
        drawOverlay(pctx, smoothedRef.current, stableCountRef.current / STABLE_FRAMES_REQUIRED);
      }
      return;
    }

    // Draw to offscreen detection canvas (video res, no DPR) and detect
    const dctx = setupDetectCanvas(detectCanvasRef.current, video);
    if (!dctx) return;
    dctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    const img = window.cv.imread(detectCanvasRef.current);
    try {
      const corners = scannerRef.current.detectCorners(img);

      if (corners) {
        missCountRef.current = 0;
        const smoothed = smoothCorners(smoothedRef.current, corners);
        smoothedRef.current = smoothed;

        if (prevCornersRef.current && cornerDelta(prevCornersRef.current, smoothed) < STABLE_THRESHOLD_PX) {
          stableCountRef.current++;
        } else {
          stableCountRef.current = 0;
        }
        prevCornersRef.current = smoothed;

        const progress = Math.min(1, stableCountRef.current / STABLE_FRAMES_REQUIRED);
        setStableProgress(progress);
        setScanState(progress >= 1 ? 'confirming' : 'detected');
        drawOverlay(pctx, smoothed, progress);
      } else {
        missCountRef.current++;
        // Keep overlay for a few missed frames to avoid flicker
        if (missCountRef.current > MISS_TOLERANCE) {
          smoothedRef.current = null;
          prevCornersRef.current = null;
          stableCountRef.current = 0;
          setStableProgress(0);
          setScanState('searching');
        } else if (smoothedRef.current) {
          drawOverlay(pctx, smoothedRef.current, stableCountRef.current / STABLE_FRAMES_REQUIRED);
        }
      }
    } finally {
      img.delete();
    }
  }, []);

  const startLoop = useCallback(() => {
    const loop = () => { tick(); animFrameRef.current = requestAnimationFrame(loop); };
    animFrameRef.current = requestAnimationFrame(loop);
  }, [tick]);

  const stopLoop = () => cancelAnimationFrame(animFrameRef.current);

  // --- Capture ---
  const captureDocument = useCallback(() => {
    stopLoop();
    const video = videoRef.current;
    const resultCanvas = resultCanvasRef.current;
    if (!video || !resultCanvas) return;

    // Draw full-res frame to a temp canvas for extraction
    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = video.videoWidth;
    tmpCanvas.height = video.videoHeight;
    const tmpCtx = tmpCanvas.getContext('2d');
    if (!tmpCtx) return;
    tmpCtx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    const result = scannerRef.current.extractPaper(tmpCanvas, smoothedRef.current, { preserveColor: true });
    if (result) {
      resultCanvas.width = result.canvas.width;
      resultCanvas.height = result.canvas.height;
      const rctx = resultCanvas.getContext('2d');
      if (rctx) rctx.drawImage(result.canvas, 0, 0);
    }
    setScanState('captured');
  }, []);

  useEffect(() => {
    if (scanState === 'confirming') captureDocument();
  }, [scanState, captureDocument]);

  // --- Load a stream from the given device, stopping any previous one ---
  const loadStream = useCallback(async (device: MediaDeviceInfo) => {
    const myGen = ++streamGenRef.current;

    // Stop any active stream before requesting a new one
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: device.deviceId,
          facingMode: 'environment',
          width: { ideal: 1920, max: 2560 },
          height: { ideal: 1080, max: 1440 },
        },
      });
    } catch (err) {
      if (myGen === streamGenRef.current) console.error('getUserMedia failed:', err);
      return;
    }

    // Stale (a newer call has started) or unmounted? Drop the new stream so it
    // doesn't leak past component lifetime.
    if (
      !isMountedRef.current ||
      myGen !== streamGenRef.current ||
      !videoRef.current
    ) {
      stream.getTracks().forEach(t => t.stop());
      return;
    }

    streamRef.current = stream;
    const video = videoRef.current;
    try { localStorage.setItem(LS_CAMERA_KEY, device.deviceId); } catch (_) { /* */ }
    video.srcObject = stream;
    video.onloadedmetadata = () => {
      if (!video.videoWidth) return;
      updateDisplaySize(video.videoWidth, video.videoHeight);
      video.play();
    };
  }, [updateDisplaySize]);

  const flipCamera = useCallback(async () => {
    if (cameras.length < 2) return;
    const next = (cameraIdx + 1) % cameras.length;
    setCameraIdx(next);
    await loadStream(cameras[next]).catch(err => console.error('Flip camera failed:', err));
  }, [cameras, cameraIdx, loadStream]);

  // --- Camera init (first load) ---
  useEffect(() => {
    isMountedRef.current = true;
    const init = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter(d => d.kind === 'videoinput');
      if (cams.length === 0 || !isMountedRef.current) return;
      setCameras(cams);
      const initial = pickInitialCamera(cams);
      setCameraIdx(initial);
      await loadStream(cams[initial]);
      if (isMountedRef.current) setTimeout(startLoop, 500);
    };
    loadOpenCv(() => init().catch(err => console.error('Camera init failed:', err)));
    return () => {
      isMountedRef.current = false;
      // Invalidate any in-flight loadStream so its resolved stream gets dropped
      streamGenRef.current++;
      stopLoop();
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      const v = videoRef.current;
      if (v) v.srcObject = null;
    };
  }, [loadOpenCv, loadStream, startLoop]);

  const resetForNextScan = useCallback(() => {
    smoothedRef.current = null;
    prevCornersRef.current = null;
    stableCountRef.current = 0;
    missCountRef.current = 0;
    setStableProgress(0);
    setScanState('searching');
    startLoop();
  }, [startLoop]);

  const handleDiscard = () => resetForNextScan();

  const handleApprove = async () => {
    if (!resultCanvasRef.current) return;
    const file = await canvasToFile(resultCanvasRef.current, `scan-${Date.now()}.jpg`);
    setScanQueue(q => [...q, file]);
    resetForNextScan();
  };

  const handleFinish = async () => {
    if (scanQueue.length === 0 || isUploading) return;
    setIsUploading(true);
    try {
      const results = await Promise.allSettled(scanQueue.map(f => uploadApi.uploadFile(f)));
      const failedIdx = results
        .map((r, i) => (r.status === 'rejected' ? i : -1))
        .filter(i => i >= 0);

      if (failedIdx.length === 0) {
        toaster.success({ title: `Saved ${scanQueue.length} document${scanQueue.length === 1 ? '' : 's'}` });
        setScanQueue([]);
        window.location.href = '/dashboard';
      } else {
        toaster.error({
          title: `${failedIdx.length} of ${scanQueue.length} failed`,
          description: 'Tap Finish to retry the failed scans',
        });
        setScanQueue(q => failedIdx.map(i => q[i]));
        setIsUploading(false);
      }
    } catch (error: any) {
      toaster.error({ title: 'Save failed', description: error.message || 'Failed to save documents' });
      setIsUploading(false);
    }
  };

  const canvasStyle = {
    width: displaySize ? `${displaySize.width}px` : '100vw',
    height: displaySize ? `${displaySize.height}px` : 'auto',
    maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain' as const,
  };

  return (
    <div className="scanner-page">
      <video ref={videoRef} style={canvasStyle} autoPlay playsInline muted hidden />
      <canvas ref={previewCanvasRef} style={canvasStyle} hidden={scanState === 'captured'} />
      <canvas ref={resultCanvasRef} style={canvasStyle} hidden={scanState !== 'captured'} />

      {scanState !== 'captured' && (
        <div style={{
          position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
          padding: '0.5rem 1.25rem', borderRadius: '9999px',
          background: scanState === 'confirming' ? 'rgba(28,27,25,0.9)' : 'rgba(0,0,0,0.6)',
          color: 'white', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.1em',
          textTransform: 'uppercase', fontFamily: 'var(--font-body)', backdropFilter: 'blur(8px)',
          transition: 'background 0.3s ease', whiteSpace: 'nowrap',
        }}>
          {scanState === 'searching' && 'Point at a document'}
          {scanState === 'detected' && 'Hold steady'}
          {scanState === 'confirming' && 'Capturing'}
        </div>
      )}

      {scanState !== 'captured' && cameras.length > 1 && (
        <button
          className="scanner-flip-btn"
          onClick={flipCamera}
          aria-label="Switch camera"
        >
          <SwitchCamera size={18} />
        </button>
      )}

      {scanState === 'detected' && stableProgress > 0 && (
        <div style={{
          position: 'fixed', top: 60, left: '50%', transform: 'translateX(-50%)',
          width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.2)', overflow: 'hidden',
        }}>
          <div style={{
            width: `${stableProgress * 100}%`, height: '100%', borderRadius: 2,
            background: '#c4b5ff', transition: 'width 0.1s linear',
          }} />
        </div>
      )}

      <button type="button" className="scanner-close-btn" onClick={() => navigate(-1)} aria-label="Close scanner">
        <X size={18} />
      </button>

      <div className="scanner-controls">
        {scanState !== 'captured' ? (
          <>
            <span className="scanner-side">
              {scanQueue.length > 0 && <span className="scanner-count">{scanQueue.length} kept</span>}
            </span>
            <button type="button" className="scanner-shutter" onClick={captureDocument} aria-label="Capture page">
              <span />
            </button>
            <span className="scanner-side scanner-side--end">
              {scanQueue.length > 0 && (
                <button type="button" className="scanner-pill scanner-pill--paper" onClick={handleFinish} disabled={isUploading}>
                  {isUploading ? 'Saving…' : 'Done'}
                </button>
              )}
            </span>
          </>
        ) : (
          <>
            <button type="button" className="scanner-pill" onClick={handleDiscard}>
              <RotateCcw size={16} /> Retake
            </button>
            <button type="button" className="scanner-pill scanner-pill--paper" onClick={handleApprove}>
              <Check size={16} /> Keep page
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export { Scanner };
